import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import ContentWMax from '../../Components/PageLayout/ContentWMax';
import PageTitle from '../../Components/PageTitle/PageTitle';
import { Alert, Input, Select, Button, Checkbox, Pagination } from 'antd';
import { useSearchParams } from 'react-router-dom';
import TaskModal from '../../Components/Task/TaskModal';
import styles from './KanbanBoard.module.scss';
import api from '../../services/api';
import LoadingOverlay from '../../Components/Loading/LoadingOverlay';
import {
    getInitialBoardSoundEnabled,
    getRolesFromStorage,
    persistBoardSoundEnabled,
    subscribeToRoles,
} from '../../services/clientAuth';
import {
    areBoardQueriesEqual,
    reconcileBoardTasks,
    isBoardGlobalQueryTooShort,
    createBoardTaskMatcher,
    normalizeBoardQuery,
} from './boardFilterUtils';
import useDebouncedValue from './useDebouncedValue';
import BoardTaskList from './BoardTaskList';

function parseAssigneeFilters(raw) {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function parseColumnSorts(raw) {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function parseActivityFilters(raw) {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function hasRole(roles, role) {
    return roles.includes(role);
}

function hasAnyRole(roles, expectedRoles) {
    return expectedRoles.some((role) => hasRole(roles, role));
}

const BOARD_MUTATION_ALLOWED_ROLES = [
    'ROLE_SPECIALIST',
    'ROLE_SPECIALIST_TS',
    'ROLE_OKK',
    'ROLE_ADMIN',
    'ROLE_SUPER_ADMIN',
    'ROLE_YK',
    'ROLE_SUPPORT',
    'ROLE_DESIGN',
    'ROLE_EXTERNAL_CASHBOX',
];

const TASK_CREATION_ALLOWED_ROLES = [
    ...BOARD_MUTATION_ALLOWED_ROLES,
    'ROLE_MANAGER',
];

const KNOWN_TEAM_TITLES_BY_ROLE = {
    ROLE_OKK: ['ОКК'],
    ROLE_SPECIALIST_TS: ['IT ТП'],
    ROLE_SPECIALIST: ['Дизайн Чио Чио'],
    ROLE_MANAGER: ['Дизайн Чио Чио'],
    ROLE_ADMIN: ['IT ТП', 'Дизайн Чио Чио', 'ОКК'],
    ROLE_SUPER_ADMIN: ['IT ТП', 'Дизайн Чио Чио', 'ОКК'],
    ROLE_YK: ['IT ТП', 'Дизайн Чио Чио', 'ОКК'],
};

function useIsDesktop(minWidth = 901) {
    const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= minWidth);

    useEffect(() => {
        const onResize = () => setIsDesktop(window.innerWidth >= minWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [minWidth]);

    return isDesktop;
}

function playNewTaskTone() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.value = 0.03;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        setTimeout(() => {
            osc.stop();
            ctx.close();
        }, 220);
    } catch {
        // no-op
    }
}

function parseBoardDate(value) {
    if (!value || typeof value !== 'string') return 0;
    const [datePart, timePart] = value.split(' ');
    if (!datePart || !timePart) return 0;
    const [dd, mm, yyyy] = datePart.split('.').map(Number);
    const [hh, min] = timePart.split(':').map(Number);
    if (!dd || !mm || !yyyy) return 0;
    return new Date(yyyy, mm - 1, dd, hh || 0, min || 0, 0).getTime();
}

function getTaskLastActivityTimestamp(task) {
    return parseBoardDate(task?.dateLastMess || task?.dateCreate);
}

function getWaitingStaffPriority(task) {
    return task?.responseState === 'waiting_staff' ? 1 : 0;
}

function matchesLastActivityFilter(task, filterValue) {
    if (!filterValue || filterValue === 'all') return true;

    const timestamp = getTaskLastActivityTimestamp(task);
    if (!timestamp) return false;

    const ageMs = Date.now() - timestamp;
    const dayMs = 24 * 60 * 60 * 1000;

    switch (filterValue) {
        case '1d':
            return ageMs <= dayMs;
        case '3d':
            return ageMs <= 3 * dayMs;
        case '7d':
            return ageMs <= 7 * dayMs;
        case '7d_plus':
            return ageMs > 7 * dayMs;
        default:
            return true;
    }
}

const ACTIVITY_FILTERABLE_STATUSES = new Set(['В работе', 'Требует внимание партнера']);
const BOARD_POLL_INTERVAL_MS = 30000;
const BOARD_PAGE_SIZE = 50;
const BOARD_GLOBAL_SEARCH_DEBOUNCE_MS = 500;

const BOARD_EXPORT_COLUMNS = [
    ['id', 'Номер'],
    ['title', 'Название'],
    ['status', 'Статус'],
    ['priority', 'Приоритет'],
    ['author', 'Автор'],
    ['assignees', 'Исполнитель'],
    ['team', 'Команда'],
    ['taskKindLabel', 'Тип задачи'],
    ['observers', 'Наблюдатели'],
    ['tags', 'Теги'],
    ['terminalNumber', 'Терминал'],
    ['contactPhone', 'Телефон'],
    ['dateCreate', 'Создана'],
    ['dateLastMess', 'Последняя активность'],
    ['responseState', 'Ожидается ответ'],
    ['terminalPaymentBlocked', 'Оплата на терминале'],
    ['rating', 'Оценка'],
];

function getLocalIsoDate() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
}

function normalizeBoardExportValue(task, key) {
    if (key === 'tags') {
        return Array.isArray(task?.tags)
            ? task.tags.map((tag) => tag?.title).filter(Boolean).join(', ')
            : '';
    }
    if (key === 'responseState') {
        if (task?.responseState === 'waiting_staff') return 'Сотрудника';
        if (task?.responseState === 'waiting_partner') return 'Партнёра';
        return '';
    }
    if (key === 'terminalPaymentBlocked') {
        return task?.terminalPaymentBlocked ? 'Заблокирована' : 'Работает';
    }
    if (key === 'rating') {
        return task?.rating?.score ?? '';
    }

    return task?.[key] ?? '';
}

function quoteCsvCell(value) {
    let text = String(value ?? '');
    // Excel interprets these prefixes as formulas, even inside a CSV file.
    if (/^[\s]*[=+\-@]/.test(text)) {
        text = `'${text}`;
    }
    return `"${text.replace(/"/g, '""')}"`;
}

function buildBoardTasksCsv(tasks) {
    const header = BOARD_EXPORT_COLUMNS.map(([, label]) => quoteCsvCell(label)).join(';');
    const rows = tasks.map((task) => BOARD_EXPORT_COLUMNS
        .map(([key]) => quoteCsvCell(normalizeBoardExportValue(task, key)))
        .join(';'));

    return `\ufeff${[header, ...rows].join('\r\n')}`;
}

export default function BoardPage() {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(getInitialBoardSoundEnabled);
    const [taskLoadError, setTaskLoadError] = useState('');

    const knownTaskIdsRef = useRef(new Set());
    const hasLoadedBoardRef = useRef(false);
    const soundEnabledRef = useRef(soundEnabled);
    const taskFetchInFlightRef = useRef(false);
    const queuedTaskFetchRef = useRef(null);
    const activeTaskFetchRef = useRef(null);

    const [urlParams, setUrlParams] = useSearchParams();
    const [searchParams, setSearchParams] = useState(() => ({
        ip: urlParams.get('ip') || urlParams.get('urName') || '',
        taskId: urlParams.get('taskId') || '',
        createdFrom: urlParams.get('createdFrom') || '',
        createdTo: urlParams.get('createdTo') || '',
    }));
    const [assigneeFilters, setAssigneeFilters] = useState(() => parseAssigneeFilters(urlParams.get('af')));
    const [teamFilter, setTeamFilter] = useState(() => urlParams.get('team') || '');
    const [observerFilter, setObserverFilter] = useState(() => urlParams.get('observer') || '');
    const [globalQuery, setGlobalQuery] = useState(() => urlParams.get('q') || '');
    const [showClosed, setShowClosed] = useState(() => urlParams.get('showClosed') === '1');
    const [mineOnly, setMineOnly] = useState(() => urlParams.get('mine') === '1');
    const [sortByColumn, setSortByColumn] = useState(() => parseColumnSorts(urlParams.get('sf')));
    const [activityFilters, setActivityFilters] = useState(() => parseActivityFilters(urlParams.get('laf')));
    const deferredSearchParams = useDeferredValue(searchParams);
    const deferredTeamFilter = useDeferredValue(teamFilter);
    const deferredObserverFilter = useDeferredValue(observerFilter);
    const debouncedGlobalQuery = useDebouncedValue(globalQuery, BOARD_GLOBAL_SEARCH_DEBOUNCE_MS);
    const isGlobalQueryDebouncing = !areBoardQueriesEqual(globalQuery, debouncedGlobalQuery);
    const isGlobalQueryTooShort = isBoardGlobalQueryTooShort(globalQuery);
    const normalizedDebouncedGlobalQuery = normalizeBoardQuery(debouncedGlobalQuery);
    const effectiveGlobalQuery = isBoardGlobalQueryTooShort(normalizedDebouncedGlobalQuery)
        ? ''
        : normalizedDebouncedGlobalQuery;

    const statuses = useMemo(
        () => ['Свободно', 'В работе', 'Требует внимание партнера', 'Закрыто', 'Отклонено'],
        []
    );

    const isDesktop = useIsDesktop(901);

    const [collapsed, setCollapsed] = useState(() => {
        const init = {};
        statuses.forEach((status) => {
            init[status] = false;
        });
        return init;
    });

    useEffect(() => {
        if (!isDesktop) return;
        setCollapsed((prev) => {
            const next = { ...prev };
            statuses.forEach((status) => {
                next[status] = false;
            });
            return next;
        });
    }, [isDesktop, statuses]);

    const [columnPages, setColumnPages] = useState({});

    useEffect(() => {
        setColumnPages((previous) => (Object.keys(previous).length > 0 ? {} : previous));
    }, [searchParams, teamFilter, observerFilter, effectiveGlobalQuery, mineOnly, assigneeFilters, activityFilters, sortByColumn]);

    const [roles, setRoles] = useState(() => getRolesFromStorage());
    const isManager = useMemo(() => hasRole(roles, 'ROLE_MANAGER'), [roles]);
    const hasBoardMutationRole = useMemo(
        () => hasAnyRole(roles, BOARD_MUTATION_ALLOWED_ROLES),
        [roles]
    );
    const canCreateTask = useMemo(
        () => hasAnyRole(roles, TASK_CREATION_ALLOWED_ROLES),
        [roles]
    );
    const isManagerOnly = isManager && !hasBoardMutationRole;
    const canEditBoard = !isManagerOnly;

    useEffect(() => {
        setRoles(getRolesFromStorage());
        return subscribeToRoles(setRoles);
    }, []);

    const hasLookupSearch =
        Boolean(deferredSearchParams.ip) ||
        Boolean(deferredSearchParams.taskId) ||
        Boolean(deferredSearchParams.createdFrom) ||
        Boolean(deferredSearchParams.createdTo) ||
        Boolean(deferredObserverFilter) ||
        Boolean(effectiveGlobalQuery);
    const includeClosedRequested = showClosed || hasLookupSearch;

    useEffect(() => {
        const next = new URLSearchParams();

        if (searchParams.ip) next.set('ip', searchParams.ip);
        if (searchParams.taskId) next.set('taskId', searchParams.taskId);
        if (searchParams.createdFrom) next.set('createdFrom', searchParams.createdFrom);
        if (searchParams.createdTo) next.set('createdTo', searchParams.createdTo);
        if (teamFilter) next.set('team', teamFilter);
        if (observerFilter) next.set('observer', observerFilter);
        if (globalQuery) next.set('q', globalQuery);
        if (Object.keys(assigneeFilters).length > 0) next.set('af', JSON.stringify(assigneeFilters));
        if (showClosed) next.set('showClosed', '1');
        if (mineOnly) next.set('mine', '1');
        if (Object.keys(sortByColumn).length > 0) next.set('sf', JSON.stringify(sortByColumn));
        if (Object.keys(activityFilters).length > 0) next.set('laf', JSON.stringify(activityFilters));

        setUrlParams(next, { replace: true });
    }, [searchParams, assigneeFilters, teamFilter, observerFilter, globalQuery, showClosed, mineOnly, sortByColumn, activityFilters, setUrlParams]);

    useEffect(() => {
        soundEnabledRef.current = soundEnabled;
        persistBoardSoundEnabled(soundEnabled);
    }, [soundEnabled]);

    useEffect(() => {
        if (!isGlobalQueryDebouncing) return;

        queuedTaskFetchRef.current = null;
        activeTaskFetchRef.current?.controller.abort();
    }, [isGlobalQueryDebouncing, globalQuery]);

    const fetchTasks = useCallback(async ({ silent = false, force = false } = {}) => {
        if (isGlobalQueryDebouncing) return;

        const filters = {
            ip: deferredSearchParams.ip,
            taskId: deferredSearchParams.taskId,
            createdFrom: deferredSearchParams.createdFrom,
            createdTo: deferredSearchParams.createdTo,
            team: deferredTeamFilter,
            observer: deferredObserverFilter,
            q: effectiveGlobalQuery,
        };
        const requestedFetch = {
            silent,
            includeClosed: includeClosedRequested,
            mineOnly,
            filters,
            key: JSON.stringify({ includeClosed: includeClosedRequested, mineOnly, filters }),
        };

        if (taskFetchInFlightRef.current) {
            const activeFetch = activeTaskFetchRef.current;
            // An aborted request cannot satisfy a retry for the same filters
            // (including React StrictMode's effect cleanup and restart).
            if (activeFetch?.key === requestedFetch.key && !activeFetch.controller.signal.aborted && !force) return;

            queuedTaskFetchRef.current = requestedFetch;
            activeTaskFetchRef.current?.controller.abort();
            return;
        }

        taskFetchInFlightRef.current = true;
        let nextFetch = requestedFetch;
        let showRefreshing = false;

        try {
            while (nextFetch) {
                const currentFetch = nextFetch;
                queuedTaskFetchRef.current = null;

                if (!currentFetch.silent && !showRefreshing) {
                    showRefreshing = true;
                    setIsRefreshing(true);
                    setTaskLoadError('');
                }

                let controller = null;
                try {
                    controller = new AbortController();
                    activeTaskFetchRef.current = { key: currentFetch.key, controller };
                    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const query = new URLSearchParams({
                        timeZone: userTimeZone,
                        includeClosed: currentFetch.includeClosed ? '1' : '0',
                        mine: currentFetch.mineOnly ? '1' : '0',
                    });
                    Object.entries(currentFetch.filters || {}).forEach(([key, value]) => {
                        if (String(value || '').trim() !== '') query.set(key, String(value).trim());
                    });
                    const response = await api.get(`/api/getTaskList?${query.toString()}`, {
                        signal: controller.signal,
                    });

                    // A newer filter/poll request is already queued. Do not render stale data.
                    if (!controller.signal.aborted && !queuedTaskFetchRef.current) {
                        const incoming = Array.isArray(response.data) ? response.data : [];
                        const shouldPlayTone =
                            hasLoadedBoardRef.current &&
                            soundEnabledRef.current &&
                            incoming.some((task) => task.status === 'Свободно' && !knownTaskIdsRef.current.has(String(task.id)));

                        knownTaskIdsRef.current = new Set(incoming.map((task) => String(task.id)));
                        hasLoadedBoardRef.current = true;

                        setTasks((prev) => reconcileBoardTasks(prev, incoming));
                        setTaskLoadError('');

                        if (shouldPlayTone) {
                            playNewTaskTone();
                        }
                    }
                } catch (error) {
                    const wasCanceled =
                        controller?.signal.aborted ||
                        error?.code === 'ERR_CANCELED' ||
                        error?.name === 'CanceledError';
                    if (!wasCanceled) {
                        console.error('Ошибка при загрузке задач:', error);
                        setTaskLoadError(
                            currentFetch.filters?.q
                                ? 'Не удалось выполнить поиск. Повторите попытку.'
                                : 'Не удалось загрузить задачи. Повторите попытку.'
                        );
                    }
                } finally {
                    if (activeTaskFetchRef.current?.controller === controller) {
                        activeTaskFetchRef.current = null;
                    }
                }

                nextFetch = queuedTaskFetchRef.current;
            }
        } finally {
            taskFetchInFlightRef.current = false;
            activeTaskFetchRef.current = null;
            if (showRefreshing) setIsRefreshing(false);
            setIsInitialLoading(false);
        }
    }, [deferredObserverFilter, deferredSearchParams, deferredTeamFilter, effectiveGlobalQuery, includeClosedRequested, isGlobalQueryDebouncing, mineOnly]);

    useEffect(() => {
        if (isGlobalQueryDebouncing) return;
        void fetchTasks({ silent: false });
    }, [fetchTasks, isGlobalQueryDebouncing]);

    useEffect(() => () => {
        queuedTaskFetchRef.current = null;
        activeTaskFetchRef.current?.controller.abort();
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'hidden') return;
            void fetchTasks({ silent: true });
        }, BOARD_POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [fetchTasks]);

    const filteredTasks = useMemo(() => {
        return (tasks || []).filter(createBoardTaskMatcher({
            ip: deferredSearchParams.ip,
            taskId: deferredSearchParams.taskId,
            team: deferredTeamFilter,
            observer: deferredObserverFilter,
        }));
    }, [deferredSearchParams, tasks, deferredTeamFilter, deferredObserverFilter]);

    const allAssignees = useMemo(() => {
        const names = new Set();
        (tasks || []).forEach((task) => {
            if (task.assignees) names.add(task.assignees);
        });
        return Array.from(names).sort();
    }, [tasks]);

    const allTeams = useMemo(() => {
        const teams = new Set();
        (tasks || []).forEach((task) => {
            if (task.team && task.team.trim() !== '') teams.add(task.team);
        });
        (roles || []).forEach((role) => {
            const knownTeams = KNOWN_TEAM_TITLES_BY_ROLE[role] || [];
            knownTeams.forEach((teamTitle) => teams.add(teamTitle));
        });
        return Array.from(teams).sort();
    }, [tasks, roles]);

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        setSearchParams((prev) => ({ ...prev, [name]: value }));
    };

    const handleAssigneeFilterChange = (status, value) => {
        setAssigneeFilters((prev) => ({ ...prev, [status]: value }));
    };

    const handleSortChange = (status, value) => {
        setSortByColumn((prev) => ({ ...prev, [status]: value || 'desc' }));
    };

    const handleActivityFilterChange = (status, value) => {
        setActivityFilters((prev) => {
            const next = { ...prev };
            if (!value || value === 'all') {
                delete next[status];
            } else {
                next[status] = value;
            }
            return next;
        });
    };

    const tasksByStatus = useMemo(() => {
        const groups = Object.create(null);
        statuses.forEach((status) => { groups[status] = []; });
        const activityTimestamps = new Map();
        filteredTasks.forEach((task) => {
            groups[task.status]?.push(task);
            activityTimestamps.set(task.id, getTaskLastActivityTimestamp(task));
        });
        statuses.forEach((status) => {
            let items = groups[status];
            const needle = assigneeFilters[status];
            if (needle) {
                const query = String(needle).toLowerCase();
                items = items.filter((task) => task.assignees && task.assignees.toLowerCase().includes(query));
            }
            const activityFilter = activityFilters[status] || 'all';
            if (ACTIVITY_FILTERABLE_STATUSES.has(status) && activityFilter !== 'all') {
                items = items.filter((task) => matchesLastActivityFilter(task, activityFilter));
            }
            const sortOrder = sortByColumn[status] || 'desc';
            items.sort((a, b) => {
                const waitingDiff = getWaitingStaffPriority(b) - getWaitingStaffPriority(a);
                if (waitingDiff !== 0) return waitingDiff;

                const aTs = activityTimestamps.get(a.id);
                const bTs = activityTimestamps.get(b.id);
                return sortOrder === 'asc' ? aTs - bTs : bTs - aTs;
            });
            groups[status] = items;
        });
        return groups;
    }, [activityFilters, assigneeFilters, filteredTasks, sortByColumn, statuses]);

    useEffect(() => {
        setColumnPages((previous) => {
            let next = previous;
            statuses.forEach((status) => {
                const lastPage = Math.max(1, Math.ceil(tasksByStatus[status].length / BOARD_PAGE_SIZE));
                if ((previous[status] || 1) > lastPage) {
                    if (next === previous) next = { ...previous };
                    next[status] = lastPage;
                }
            });
            return next;
        });
    }, [tasksByStatus, statuses]);

    const exportableTasks = useMemo(
        () => statuses.flatMap((status) => tasksByStatus[status]),
        [tasksByStatus, statuses]
    );

    const handleExportTasks = useCallback(() => {
        if (exportableTasks.length === 0) return;

        const csv = buildBoardTasksCsv(exportableTasks);
        const objectUrl = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const anchor = document.createElement('a');
        const from = searchParams.createdFrom || 'all';
        const to = searchParams.createdTo || getLocalIsoDate();

        anchor.href = objectUrl;
        anchor.download = `support-tasks-${from}-${to}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }, [exportableTasks, searchParams.createdFrom, searchParams.createdTo]);

    const toggleCollapsed = (status) => {
        if (isDesktop) return;
        setCollapsed((prev) => ({ ...prev, [status]: !prev[status] }));
    };

    const onDragStart = useCallback((e, id) => {
        if (!canEditBoard) return;
        e.dataTransfer.setData('taskId', id);
    }, [canEditBoard]);

    const onDragOver = (e) => {
        if (!canEditBoard) return;
        e.preventDefault();
    };

    const onDrop = (e, status) => {
        if (!canEditBoard) return;
        const taskId = e.dataTransfer.getData('taskId');
        const updated = (tasks || []).map((task) => (String(task.id) === String(taskId) ? { ...task, status } : task));
        setTasks(updated);
        void saveTaskStatus(taskId, status);
    };

    const saveTaskStatus = async (taskId, status) => {
        try {
            await api.put(`/postTask/${taskId}`, { status });
        } catch (error) {
            console.error(error);
        }
    };

    const openModal = () => setModalIsOpen(true);
    const closeModal = () => setModalIsOpen(false);

    const handleCreateTask = async (taskData) => {
        const endpoint = taskData.endpoint || '/setTask';
        const newTask = endpoint === '/api/tasks/okk'
            ? {
                title: taskData.title,
                taskKind: taskData.taskKind || 'common',
                author: taskData.author,
                assignee: taskData.assignee,
                observers: taskData.observers || [],
                priority: taskData.priority,
                terminalNumber: taskData.terminalNumber || '',
                estimatedResolutionDate: taskData.estimatedResolutionDate || null,
                text: taskData.text,
                imageUrl: taskData.imageUrl,
                teamCode: taskData.teamCode || 'OKK',
            }
            : {
                title: taskData.title,
                taskKind: taskData.taskKind || 'common',
                author: taskData.author,
                assignee: taskData.assignee,
                observers: taskData.observers || [],
                description: taskData.description,
                priority: taskData.priority,
                team: taskData.team,
                status: 'Свободно',
                terminalNumber: taskData.terminalNumber || '',
                terminalPaymentBlocked: Boolean(taskData.terminalPaymentBlocked),
                estimatedResolutionDate: taskData.estimatedResolutionDate || null,
            };

        try {
            const response = await api.post(endpoint, newTask);
            const data = response.data;
            if (data?.id != null) {
                knownTaskIdsRef.current.add(String(data.id));
            }
            if (data?.telegramDelivery === 'failed') {
                window.alert(`Тикет создан, но Telegram не отправился: ${data.telegramError || 'неизвестная ошибка'}`);
            }
            await fetchTasks({ silent: true, force: true });
            closeModal();
        } catch (error) {
            console.error('Ошибка при создании задачи:', error);
        }
    };

    return (
        <ContentWMax>
            <PageTitle title="Доска" />

            <div className={styles.page}>
                <div className={styles.toolbar}>
                    <div className={styles.filters}>
                        <Input
                            placeholder="Поиск по ИП (автор/исполнитель)"
                            name="ip"
                            value={searchParams.ip}
                            onChange={handleSearchChange}
                            className={styles.input}
                        />
                        <Input
                            placeholder="Поиск по номеру заявки"
                            name="taskId"
                            value={searchParams.taskId}
                            onChange={handleSearchChange}
                            className={styles.inputSmall}
                            inputMode="numeric"
                        />
                        <div className={styles.periodFilter}>
                            <Input
                                type="date"
                                aria-label="Дата создания с"
                                name="createdFrom"
                                value={searchParams.createdFrom}
                                onChange={handleSearchChange}
                                className={styles.dateInput}
                            />
                            <Input
                                type="date"
                                aria-label="Дата создания по"
                                name="createdTo"
                                value={searchParams.createdTo}
                                onChange={handleSearchChange}
                                className={styles.dateInput}
                            />
                        </div>
                        <Select
                            placeholder="Команда"
                            className={styles.select}
                            allowClear
                            value={teamFilter || undefined}
                            onChange={(value) => setTeamFilter(value || '')}
                            options={[
                                { value: '', label: 'Все команды' },
                                ...allTeams.map((teamName) => ({ value: teamName, label: teamName })),
                            ]}
                        />
                        <Input
                            placeholder="Поиск по наблюдателям"
                            value={observerFilter}
                            onChange={(e) => setObserverFilter(e.target.value)}
                            className={styles.input}
                        />
                        <div className={styles.globalSearch}>
                            <Input
                                placeholder="Поиск по ключевым словам среди обращений"
                                value={globalQuery}
                                onChange={(e) => {
                                    setGlobalQuery(e.target.value);
                                    setTaskLoadError('');
                                }}
                                className={styles.input}
                                status={isGlobalQueryTooShort ? 'warning' : undefined}
                                aria-describedby={isGlobalQueryTooShort ? 'board-global-search-hint' : undefined}
                            />
                            {isGlobalQueryTooShort && (
                                <span id="board-global-search-hint" className={styles.filterHint}>
                                    Введите минимум 3 символа
                                </span>
                            )}
                        </div>
                        <Checkbox checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)}>
                            Мои задачи
                        </Checkbox>
                    </div>

                    <div className={styles.toolbarRight}>
                        <Button
                            onClick={() => void fetchTasks({ force: true })}
                            className={styles.refreshBtn}
                            loading={isRefreshing}
                            disabled={isGlobalQueryDebouncing}
                        >
                            Обновить
                        </Button>
                        <Button
                            onClick={handleExportTasks}
                            className={styles.exportBtn}
                            disabled={isInitialLoading || exportableTasks.length === 0}
                            title={`Выгрузить задач: ${exportableTasks.length}`}
                        >
                            Экспорт задач
                        </Button>
                        <Button onClick={() => setSoundEnabled((prev) => !prev)} className={styles.soundBtn}>
                            {soundEnabled ? 'Звук: вкл' : 'Звук: выкл'}
                        </Button>

                        {canCreateTask && (
                            <Button type="primary" onClick={openModal} className={styles.createBtn}>
                                Создать задачу
                            </Button>
                        )}
                    </div>
                </div>

                {taskLoadError && (
                    <Alert
                        className={styles.errorAlert}
                        type="error"
                        showIcon
                        message={taskLoadError}
                        action={(
                            <Button size="small" onClick={() => void fetchTasks({ force: true })}>
                                Повторить
                            </Button>
                        )}
                    />
                )}

                <div className={styles.boardWrap}>
                    {(isInitialLoading || isRefreshing || isGlobalQueryDebouncing) && (
                        <LoadingOverlay text={globalQuery ? 'Ищем задачи...' : 'Загружаем задачи...'} />
                    )}

                    <div className={styles.legend}>
                        <div className={styles.legendItem}>
                            <span className={`${styles.legendDot} ${styles.legendWaitingStaff}`} />
                            Ждем ответа сотрудника
                        </div>
                        <div className={styles.legendItem}>
                            <span className={`${styles.legendDot} ${styles.legendWaitingPartner}`} />
                            Ждем ответа партнера
                        </div>
                    </div>

                    <div className={styles.columns}>
                        {statuses.map((status) => {
                            const columnTasks = tasksByStatus[status];
                            const allCount = columnTasks.length;
                            const page = Math.min(columnPages[status] || 1, Math.max(1, Math.ceil(allCount / BOARD_PAGE_SIZE)));
                            const isCollapsed = !!collapsed[status];
                            const isClosedColumn = status === 'Закрыто' || status === 'Отклонено';
                            const mustShowLoadClosedButton = isClosedColumn && !includeClosedRequested;

                            return (
                                <div
                                    key={status}
                                    className={styles.column}
                                    onDragOver={onDragOver}
                                    onDrop={(e) => onDrop(e, status)}
                                    data-readonly={!canEditBoard ? 'true' : 'false'}
                                >
                                    <button
                                        type="button"
                                        className={styles.columnHeaderBtn}
                                        onClick={() => toggleCollapsed(status)}
                                        aria-expanded={!isCollapsed}
                                        data-desktop={isDesktop ? 'true' : 'false'}
                                    >
                                        <div className={styles.columnHeaderLeft}>
                                            <div className={styles.columnTitle}>{status}</div>
                                            <div className={styles.columnCount}>{allCount}</div>
                                        </div>
                                        <div className={styles.chevron} data-open={!isCollapsed ? 'true' : 'false'} />
                                    </button>

                                    {!isCollapsed && (
                                        <>
                                            {mustShowLoadClosedButton ? (
                                                <Button onClick={() => setShowClosed(true)} className={styles.showMoreBtn}>
                                                    {status === 'Закрыто'
                                                        ? 'Показать закрытые задачи'
                                                        : 'Показать отклоненные задачи'}
                                                </Button>
                                            ) : (
                                                <>
                                                    <Select
                                                        placeholder="Исполнитель"
                                                        className={styles.assigneeSelect}
                                                        allowClear
                                                        value={assigneeFilters[status] || undefined}
                                                        onChange={(value) => handleAssigneeFilterChange(status, value)}
                                                        options={allAssignees.map((assignee) => ({ value: assignee, label: assignee }))}
                                                    />
                                                    <Select
                                                        className={styles.sortSelect}
                                                        value={sortByColumn[status] || 'desc'}
                                                        onChange={(value) => handleSortChange(status, value)}
                                                        options={[
                                                            { value: 'desc', label: 'Сначала свежие' },
                                                            { value: 'asc', label: 'Сначала старые' },
                                                        ]}
                                                    />
                                                    {ACTIVITY_FILTERABLE_STATUSES.has(status) && (
                                                        <Select
                                                            className={styles.activitySelect}
                                                            value={activityFilters[status] || 'all'}
                                                            onChange={(value) => handleActivityFilterChange(status, value)}
                                                            options={[
                                                                { value: 'all', label: 'Активность: все' },
                                                                { value: '1d', label: 'Активность: за 24 часа' },
                                                                { value: '3d', label: 'Активность: до 3 дней' },
                                                                { value: '7d', label: 'Активность: до 7 дней' },
                                                                { value: '7d_plus', label: 'Активность: старше 7 дней' },
                                                            ]}
                                                        />
                                                    )}

                                                    {allCount > BOARD_PAGE_SIZE && (
                                                        <Pagination
                                                            className={styles.columnPagination}
                                                            aria-label={`Страницы колонки «${status}»`}
                                                            simple
                                                            current={page}
                                                            pageSize={BOARD_PAGE_SIZE}
                                                            total={allCount}
                                                            showSizeChanger={false}
                                                            onChange={(nextPage) => setColumnPages((prev) => ({ ...prev, [status]: nextPage }))}
                                                        />
                                                    )}
                                                    <BoardTaskList
                                                        tasks={columnTasks}
                                                        page={page}
                                                        pageSize={BOARD_PAGE_SIZE}
                                                        canEditBoard={canEditBoard}
                                                        onDragStart={onDragStart}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <TaskModal isOpen={modalIsOpen} onRequestClose={closeModal} onCreateTask={handleCreateTask} />
            </div>
        </ContentWMax>
    );
}
